import { Injectable } from '@angular/core';
import { HubConnection } from '@aspnet/signalr';
import { HubConnectionService } from '../hub-connection.service';
import * as signalR from '@aspnet/signalr';
import { Subject } from 'rxjs';
import { UrlBuilderService } from 'projects/communication/src/app/services/url-builder.service';
import { IChatMessageReceptor } from 'src/app/modules/messenger/interfaces/chat-message-receptor';
import { IMachineStatusChange } from '../../../interfaces/signal-r/machine-status-change';
import { AuthenticationService } from 'projects/authentication/src/app/services';

@Injectable({
  providedIn: 'root',
})
export class SignalRMachineService {
  private hubStatusConnection: HubConnection | undefined;
  private hubTrackingConnection: HubConnection | undefined;
  private hubOperatorConnection: HubConnection | undefined;
  private hubDrillingConnection: HubConnection | undefined;
  private hubLoopConnection: HubConnection | undefined;
  private hubTruckLoopConnection: HubConnection | undefined;

  private baseUrl: string;

  public messageGetter: Subject<IChatMessageReceptor>;
  public statusChange: Subject<IMachineStatusChange>;
  public trackingChange: Subject<any>;
  public operatorChange: Subject<any>;
  public drillingChange: Subject<any>;
  public loopChange: Subject<any>;
  public truckLoopChange: Subject<any>;

  constructor(
    private hubService: HubConnectionService,
    private authService: AuthenticationService,
    private urlBuilderService: UrlBuilderService,
  ) {
    this.messageGetter = new Subject<IChatMessageReceptor>();
    this.statusChange = new Subject<IMachineStatusChange>();
    this.trackingChange = new Subject<any>();
    this.operatorChange = new Subject<any>();
    this.drillingChange = new Subject<any>();
    this.loopChange = new Subject<any>();
    this.truckLoopChange = new Subject<any>();
    this.baseUrl = this.urlBuilderService.socketUrl;
  }

  /**
   * Function to destroy all signal r connections referenced to map
   */
  public disconnectAllMapHubs(): void {
    this.hubService.stopConnection(this.hubTrackingConnection);
    this.hubService.stopConnection(this.hubStatusConnection);
    this.hubService.stopConnection(this.hubOperatorConnection);
    this.hubService.stopConnection(this.hubDrillingConnection);
    this.hubService.stopConnection(this.hubLoopConnection);
    this.hubService.stopConnection(this.hubTruckLoopConnection);
  }

  /**
   * Function to destroy all signal r connections referenced to messenger
   */
  public disconnectAllMessengerHubs(): void {
    this.hubService.stopConnection(this.hubStatusConnection);
    this.hubService.stopConnection(this.hubTrackingConnection);
  }

  /**
   * Function to connect signal r and web application - machine status changes
   */
  public initMachineStatusChange(count = 1): void {
    this.hubService.stopConnection(this.hubStatusConnection).then(() => {
      this.hubStatusConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${this.baseUrl}/status`, {
          accessTokenFactory: () => this.hubService.getToken(),
          transport: signalR.HttpTransportType.LongPolling,
        })
        .configureLogging(signalR.LogLevel.Information)
        .build();

      this.hubStatusConnection.on('lhd-status', (status) => this.statusChange.next(status));

      this.hubStatusConnection
        .start()
        .then(() => {
          console.log('Conexión al hub lhd-status iniciada correctamente.');
        })
        .catch((err) => {
          console.error('Error en el handshake de SignalR: lhd-status', err);
          if (err?.statusCode === 401) {
            this.authService.cleanSession();
          } else if (count <= 3) {
            setTimeout(() => this.initMachineStatusChange(count + 1), 3000);
          } else {
            console.error('Error no resuelto después de 3 intentos:', err);
          }
        });
    });
  }

  /**
   * Function to connect signal r and web application - machine drilling changes
   */
  public initNewDrilling(count = 1): void {
    this.hubService
      .stopConnection(this.hubDrillingConnection)
      .then(() => {
        this.hubDrillingConnection = new signalR.HubConnectionBuilder()
          .withUrl(`${this.baseUrl}/drilling`, {
            accessTokenFactory: () => this.hubService.getToken(),
            transport: signalR.HttpTransportType.LongPolling,
          })
          .configureLogging(signalR.LogLevel.Information)
          .build();

        this.hubDrillingConnection.on('lhd-drilling', (drilling) => this.drillingChange.next(drilling));

        this.hubDrillingConnection
          .start()
          .then(() => {
            console.log('Conexión al hub lhd-drilling iniciada correctamente.');
          })
          .catch((err) => {
            console.error('Error en el handshake de SignalR: lhd-drilling', err);
            if (err?.statusCode === 401) {
              this.authService.cleanSession();
            } else if (count <= 3) {
              setTimeout(() => this.initNewDrilling(count + 1), 3000);
            } else {
              console.error('Error no resuelto después de 3 intentos:', err);
            }
          });
      })
      .catch((err) => {
        console.error('Error deteniendo la conexión:', err);
      });
  }

  /**
   * Function to connect signal r and web application - machine operator changes
   */
  public initOperatorChange(count = 1): void {
    this.hubService
      .stopConnection(this.hubOperatorConnection)
      .then(() => {
        this.hubOperatorConnection = new signalR.HubConnectionBuilder()
          .withUrl(`${this.baseUrl}/operators`, {
            accessTokenFactory: () => this.hubService.getToken(),
            transport: signalR.HttpTransportType.LongPolling,
          })
          .configureLogging(signalR.LogLevel.Information)
          .build();

        this.hubOperatorConnection.on('lhd-operators', (operator) => this.operatorChange.next(operator));

        this.hubOperatorConnection
          .start()
          .then(() => {
            console.log('Conexión al hub lhd-operators iniciada correctamente.');
          })
          .catch((err) => {
            console.error('Error en el handshake de SignalR: lhd-operators', err);
            if (err?.statusCode === 401) {
              this.authService.cleanSession();
            } else if (count <= 3) {
              setTimeout(() => this.initOperatorChange(count + 1), 3000);
            } else {
              console.error('Error no resuelto después de 3 intentos:', err);
            }
          });
      })
      .catch((err) => {
        console.error('Error deteniendo la conexión:', err);
      });
  }

  /**
   * Function to connect signal r and web application - machine tracking changes
   */
  public initTrackingChange(count = 1): void {
    this.hubService
      .stopConnection(this.hubTrackingConnection)
      .then(() => {
        this.hubTrackingConnection = new signalR.HubConnectionBuilder()
          .withUrl(`${this.baseUrl}/tracking`, {
            accessTokenFactory: () => this.hubService.getToken(),
            transport: signalR.HttpTransportType.LongPolling,
          })
          .configureLogging(signalR.LogLevel.Information)
          .build();

        this.hubTrackingConnection.on('lhd-tracking', (tracking) => this.trackingChange.next(tracking));

        this.hubTrackingConnection
          .start()
          .then(() => {
            console.log('Conexión al hub lhd-tracking iniciada correctamente.');
          })
          .catch((err) => {
            console.error('Error en el handshake de SignalR: lhd-tracking', err);
            if (err?.statusCode === 401) {
              this.authService.cleanSession();
            } else if (count <= 3) {
              setTimeout(() => this.initTrackingChange(count + 1), 3000);
            } else {
              console.error('Error no resuelto después de 3 intentos:', err);
            }
          });
      })
      .catch((err) => {
        console.error('Error deteniendo la conexión:', err);
      });
  }

  /**
   * Function to connect signal r and web application - machine drilling changes
   */
  public initNewLoop(count = 1): void {
    this.hubService
      .stopConnection(this.hubLoopConnection)
      .then(() => {
        this.hubLoopConnection = new signalR.HubConnectionBuilder()
          .withUrl(`${this.baseUrl}/loop`, {
            accessTokenFactory: () => this.hubService.getToken(),
            transport: signalR.HttpTransportType.LongPolling,
          })
          .configureLogging(signalR.LogLevel.Information)
          .build();

        this.hubLoopConnection.on('lhd-loop', (loop) => this.loopChange.next(loop));

        this.hubLoopConnection
          .start()
          .then(() => {
            console.log('Conexión al hub lhd-loop iniciada correctamente.');
          })
          .catch((err) => {
            console.error('Error en el handshake de SignalR: lhd-loop', err);
            if (err?.statusCode === 401) {
              this.authService.cleanSession();
            } else if (count <= 3) {
              setTimeout(() => this.initNewLoop(count + 1), 3000);
            } else {
              console.error('Error no resuelto después de 3 intentos:', err);
            }
          });
      })
      .catch((err) => {
        console.error('Error deteniendo la conexión:', err);
      });
  }

  public initTruckLoop(count = 1): void {
    this.hubService
      .stopConnection(this.hubTruckLoopConnection)
      .then(() => {
        this.hubTruckLoopConnection = new signalR.HubConnectionBuilder()
          .withUrl(`${this.baseUrl}/looptruck`, {
            accessTokenFactory: () => this.hubService.getToken(),
            transport: signalR.HttpTransportType.LongPolling,
          })
          .configureLogging(signalR.LogLevel.Information)
          .build();

        this.hubTruckLoopConnection.on('truck-loop', (loop) => this.truckLoopChange.next(loop));

        this.hubTruckLoopConnection
          .start()
          .then(() => {
            console.log('Conexión al hub truck-loop iniciada correctamente.');
          })
          .catch((err) => {
            console.error('Error en el handshake de SignalR: truck-loop', err);
            if (err?.statusCode === 401) {
              this.authService.cleanSession();
            } else if (count <= 3) {
              setTimeout(() => this.initNewLoop(count + 1), 3000);
            } else {
              console.error('Error no resuelto después de 3 intentos:', err);
            }
          });
      })
      .catch((err) => {
        console.error('Error deteniendo la conexión:', err);
      });
  }
}
